import { supabase } from '../lib/supabase';
import toastService from '../components/ui/Toast';

// Helper to convert snake_case to camelCase
function toCamelCase(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
}

export const platformMetricsService = {
  // Get comprehensive platform metrics
  async getMetrics(timeRange = '30d') {
    try {
      // Calculate date range
      const now = new Date();
      const daysAgo = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
      const startDate = new Date(now.setDate(now.getDate() - daysAgo))?.toISOString();

      // Fetch all metrics in parallel
      const [
        usersResult,
        listingsResult,
        bookingsResult,
        revenueResult,
        disputesResult,
        verificationResult,
        bookingVolumeResult,
        userStatsResult
      ] = await Promise.all([
        // Total users
        supabase?.from('user_profiles')?.select('*', { count: 'exact', head: true }),
        
        // Active listings
        supabase?.from('trailers')?.select('*', { count: 'exact', head: true })?.eq('status', 'active'),
        
        // Total bookings in time range
        supabase?.from('bookings')?.select('*', { count: 'exact', head: true })?.gte('created_at', startDate),
        
        // Revenue data
        supabase?.from('bookings')?.select('total_amount, created_at')?.gte('created_at', startDate),
        
        // Pending disputes
        supabase?.from('disputes')?.select('*', { count: 'exact', head: true })?.eq('status', 'pending'),
        
        // Verification requests
        supabase?.from('user_profiles')?.select('*', { count: 'exact', head: true })?.eq('verification_status', 'pending'),
        
        // Booking volume by status
        supabase?.from('bookings')?.select('status')?.gte('created_at', startDate),
        
        // User statistics by role
        supabase?.from('user_profiles')?.select('role, created_at')
      ]);

      // Calculate revenue
      const totalRevenue = revenueResult?.data?.reduce((sum, booking) => sum + (booking?.total_amount || 0), 0) || 0;

      // Calculate booking volume by status
      const bookingVolume = {
        confirmed: bookingVolumeResult?.data?.filter(b => b?.status === 'confirmed')?.length || 0,
        completed: bookingVolumeResult?.data?.filter(b => b?.status === 'completed')?.length || 0,
        cancelled: bookingVolumeResult?.data?.filter(b => b?.status === 'cancelled')?.length || 0,
        pending: bookingVolumeResult?.data?.filter(b => b?.status === 'pending')?.length || 0
      };

      // Calculate user statistics by role
      const userStatistics = {
        renters: userStatsResult?.data?.filter(u => u?.role === 'renter')?.length || 0,
        owners: userStatsResult?.data?.filter(u => u?.role === 'owner')?.length || 0,
        admins: userStatsResult?.data?.filter(u => u?.role === 'admin')?.length || 0,
        newUsersThisPeriod: userStatsResult?.data?.filter(u => new Date(u?.created_at) >= new Date(startDate))?.length || 0
      };

      // Calculate revenue trends (weekly breakdown)
      const revenueTrends = calculateRevenueTrends(revenueResult?.data, daysAgo);

      const metrics = {
        totalUsers: usersResult?.count || 0,
        activeListings: listingsResult?.count || 0,
        totalBookings: bookingsResult?.count || 0,
        monthlyRevenue: totalRevenue,
        pendingDisputes: disputesResult?.count || 0,
        verificationRequests: verificationResult?.count || 0,
        bookingVolume,
        userStatistics,
        revenueTrends
      };

      // Check thresholds and trigger notifications if needed
      await checkMetricsThresholds(metrics);

      return metrics;
    } catch (error) {
      console.error('Get metrics error:', error);
      throw error;
    }
  },

  // Subscribe to real-time metrics updates
  subscribeToMetrics(callback) {
    // Subscribe to multiple tables for comprehensive updates
    const channels = [
      supabase
        ?.channel('user-profiles-changes')
        ?.on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'user_profiles' },
          () => callback('user_profiles')
        )
        ?.subscribe(),
      
      supabase
        ?.channel('bookings-changes')
        ?.on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'bookings' },
          () => callback('bookings')
        )
        ?.subscribe(),
      
      supabase
        ?.channel('disputes-changes')
        ?.on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'disputes' },
          () => callback('disputes')
        )
        ?.subscribe(),
      
      supabase
        ?.channel('trailers-changes')
        ?.on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'trailers' },
          () => callback('trailers')
        )
        ?.subscribe()
    ];

    return () => {
      channels?.forEach(channel => supabase?.removeChannel(channel));
    };
  },

  // Get metrics thresholds
  async getMetricsThresholds() {
    try {
      const { data, error } = await supabase
        ?.from('metrics_thresholds')
        ?.select('*')
        ?.order('metric_name');

      if (error) {
        console.error('Get metrics thresholds error:', error);
        throw new Error(error?.message || 'Failed to fetch metrics thresholds');
      }

      return data ? data?.map(toCamelCase) : [];
    } catch (error) {
      console.error('Get metrics thresholds error:', error);
      throw error;
    }
  },

  // Update metrics threshold
  async updateMetricsThreshold(metricName, threshold) {
    try {
      const { data, error } = await supabase
        ?.from('metrics_thresholds')
        ?.update({
          threshold_value: threshold?.thresholdValue,
          comparison_operator: threshold?.comparisonOperator,
          notification_enabled: threshold?.notificationEnabled,
          notification_emails: threshold?.notificationEmails,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('metric_name', metricName)
        ?.select()
        ?.single();

      if (error) {
        console.error('Update metrics threshold error:', error);
        throw new Error(error?.message || 'Failed to update metrics threshold');
      }

      toastService?.success('Metrics threshold updated successfully');
      return toCamelCase(data);
    } catch (error) {
      console.error('Update metrics threshold error:', error);
      toastService?.error('Failed to update metrics threshold');
      throw error;
    }
  }
};

// Helper function to calculate revenue trends
function calculateRevenueTrends(revenueData, daysAgo) {
  if (!revenueData || revenueData?.length === 0) return [];

  const weeks = Math.ceil(daysAgo / 7);
  const trends = [];

  for (let i = 0; i < weeks; i++) {
    const weekStart = new Date();
    weekStart?.setDate(weekStart?.getDate() - (daysAgo - (i * 7)));
    const weekEnd = new Date(weekStart);
    weekEnd?.setDate(weekEnd?.getDate() + 7);

    const weekRevenue = revenueData
      ?.filter(booking => {
        const bookingDate = new Date(booking?.created_at);
        return bookingDate >= weekStart && bookingDate < weekEnd;
      })
      ?.reduce((sum, booking) => sum + (booking?.total_amount || 0), 0);

    trends?.push({
      week: `Week ${i + 1}`,
      revenue: weekRevenue,
      startDate: weekStart?.toISOString(),
      endDate: weekEnd?.toISOString()
    });
  }

  return trends;
}

// Helper function to check thresholds and trigger notifications
async function checkMetricsThresholds(metrics) {
  try {
    const { data: thresholds, error } = await supabase
      ?.from('metrics_thresholds')
      ?.select('*')
      ?.eq('notification_enabled', true);

    if (error || !thresholds) return;

    for (const threshold of thresholds) {
      const metricValue = getMetricValue(metrics, threshold?.metric_name);
      const shouldTrigger = evaluateThreshold(metricValue, threshold?.threshold_value, threshold?.comparison_operator);

      if (shouldTrigger) {
        // Check if notification was recently sent (within last hour)
        const lastTriggered = threshold?.last_triggered_at ? new Date(threshold?.last_triggered_at) : null;
        const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);

        if (!lastTriggered || lastTriggered < oneHourAgo) {
          // Send notification via Resend
          await sendThresholdNotification(threshold, metricValue);

          // Update last triggered timestamp
          await supabase
            ?.from('metrics_thresholds')
            ?.update({ last_triggered_at: new Date()?.toISOString() })
            ?.eq('metric_name', threshold?.metric_name);
        }
      }
    }
  } catch (error) {
    console.error('Check metrics thresholds error:', error);
  }
}

// Helper to get metric value by name
function getMetricValue(metrics, metricName) {
  const metricMap = {
    'total_users': metrics?.totalUsers,
    'pending_disputes': metrics?.pendingDisputes,
    'monthly_revenue': metrics?.monthlyRevenue,
    'verification_requests': metrics?.verificationRequests,
    'active_listings': metrics?.activeListings,
    'total_bookings': metrics?.totalBookings
  };
  return metricMap?.[metricName] || 0;
}

// Helper to evaluate threshold condition
function evaluateThreshold(value, threshold, operator) {
  switch (operator) {
    case '>': return value > threshold;
    case '<': return value < threshold;
    case '>=': return value >= threshold;
    case '<=': return value <= threshold;
    case '=': return value === threshold;
    default: return false;
  }
}

// Helper to send threshold notification via Resend
async function sendThresholdNotification(threshold, currentValue) {
  try {
    await supabase?.functions?.invoke('send-metrics-notification', {
      body: {
        metricName: threshold?.metric_name,
        thresholdValue: threshold?.threshold_value,
        currentValue,
        comparisonOperator: threshold?.comparison_operator,
        recipientEmails: threshold?.notification_emails
      }
    });

    toastService?.warning(`Threshold alert: ${threshold?.metric_name} reached ${currentValue}`);
  } catch (error) {
    console.error('Send threshold notification error:', error);
  }
}

export default platformMetricsService;